import React, { FC, useState, useContext, memo, useEffect } from 'react';
import injectSheet from 'react-jss';
import { Link, useRouter } from 'Utils/router';

// This is a hook to generate the ZenDeskJwt
import { Button } from 'Components/Buttons';
import Avatar from 'Assets/ui/avatar.svg';
import Card from 'HOC/Card';
import Help from 'Assets/ui/help.svg';
import SearchableDropdown from 'Components/SearchableDropdown';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { areEqualObjects } from 'Utils/areEqual';
import {
  useZenDeskJwtLazy,
  zenDeskBaseUrl,
} from '../../../../hooks/useZenDeskJwt';
import styleSheet from './styles';

interface Props {
  firstName: string;
  lastName: string;
  email: string;
  changeUniversity: any;
  classes?: any;
  profileData: any;
}

const cardStyles = {
  paddingLeft: '32px',
  paddingRight: '24px',
  paddingTop: '12px',
  paddingBottom: '24px',
  borderRadius: '8px',
  width: '304px',
  boxShadow: '0 2px 8px 0 rgba(5,29,57,0.1)',
};
const yvCmsUrl = process.env.REACT_APP_YV_CMS_URL;
const optimizeUrl = process.env.REACT_APP_OPTIMIZE_URL;

const Profile: FC<Props> = memo((props: Props) => {
  const [zenDeskUrl, setZenDeskUrl] = useState('');
  const {
    getJwt,
    data: lazyZenData,
    loading: lazyZenLoading,
  } = useZenDeskJwtLazy();

  useEffect(() => {
    if (lazyZenLoading) return;
    if (!lazyZenLoading && lazyZenData) {
      // Build the url, with the jwt
      const token = lazyZenData?.zen?.user?.jwt?.token;
      const tempUrl = `${zenDeskBaseUrl}${token}`;

      if (zenDeskUrl !== tempUrl) {
        // Update with the new jwt
        setZenDeskUrl(tempUrl);
      }
    } else getJwt(); // Generate a jwt
  }, [lazyZenLoading, lazyZenData]);
  const { firstName, lastName, email, profileData, changeUniversity, classes } =
    props;

  const [profileMenu, showProfileMenu] = useState(false);
  const [selected, setSelected] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const {
    helpers: { View },
    actions: { track },
  } = useContext(ProductAnalyticsContext);
  const { location } = useRouter();

  const handleBlur = (event: any) => {
    if (
      !event.currentTarget.contains(event.relatedTarget) &&
      event.target.name !== 'selectionInput'
    ) {
      showProfileMenu(false);
    }
  };
  const helpClick = () => {
    // Combined Analytics event tracking
    track('help_button_clicked', {
      screen: View(location.pathname),
    });

    // Fetch a new ZenDesk jwt
    getJwt();
  };
  const goToCMSClick = () => {
    track('navigate_to_cms_clicked', {
      screen: View(location.pathname),
    });
  };
  const goToAriaOptimizeClick = () => {
    track('navigate_to_aria_optimize_clicked', {
      screen: View(location.pathname),
    });
  };

  const onDropdownChange = (event: any) => {
    let newSelected = [...selected];

    if (selected.includes(event.target.value)) {
      newSelected = newSelected.filter((item) => {
        return item !== event.target.value;
      });
    } else {
      newSelected.push(event.target.value);
    }
    setSelected(newSelected);
    setDefaultValue(event.target.value.label);

    const chosenUniv = profileData.univList.find(
      (univ: any) => univ.label === event.target.value.label
    );

    changeUniversity({
      variables: { inst_id: String(chosenUniv.id) },
    });
  };

  const onSearchBarChange = (event: any) => {
    setInputValue(event.target.value);
  };

  const profileMenuShow = () => showProfileMenu(true);
  const profileMenuHide = () => showProfileMenu(false);

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        top: '3px',
      }}
      onMouseEnter={profileMenuShow}
      onMouseLeave={profileMenuHide}
      onFocus={profileMenuShow}
      onBlur={handleBlur}
    >
      <div className={classes.profile}>
        <Avatar className={classes.icon} />
        <span className={classes.profileName}>{`${firstName}`}</span>
        &nbsp;
        <span className={classes.profileName}>{`${lastName}`}</span>
      </div>
      {profileMenu && (
        <div
          style={{
            position: 'absolute',
            zIndex: 100,
            top: '25px',
            right: '0px',
            borderTop: '15px solid transparent',
          }}
        >
          <Card overrideStyles={cardStyles}>
            <div className={classes.titleContainer}>
              <span className={classes.menuTitle}>Account Settings</span>
              <span className={classes.help}>
                <a
                  onClick={helpClick}
                  href={`${zenDeskBaseUrl}${zenDeskUrl}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Help />
                  <span className={classes.helpText}> Help </span>
                </a>
              </span>
            </div>
            <div className={classes.heading}>Name</div>
            <div className={classes.value}>
              &nbsp;
              <span style={{ textTransform: 'capitalize' }}>
                {`${firstName}`}
              </span>
              &nbsp;
              <span style={{ textTransform: 'capitalize' }}>
                {`${lastName}`}
              </span>
            </div>
            <div className={classes.heading}>Institution Name</div>

            {profileData.univList.length === 0 && (
              <div className={classes.value}>{profileData.instName}</div>
            )}
            {profileData.univList.length > 0 && (
              <SearchableDropdown
                selectedOptions={selected}
                onDropdownChange={onDropdownChange}
                onSearchBarChange={onSearchBarChange}
                items={profileData.univList.filter((item: any) =>
                  item.label.toLowerCase().includes(inputValue.toLowerCase())
                )}
                defaultValue={defaultValue || profileData.instName}
                inputValue={inputValue}
              />
            )}

            <div className={classes.heading}>Email</div>
            <div className={classes.value}>{email}</div>
            <ul className={classes.platforms}>
              <li>
                <a
                  onClick={goToCMSClick}
                  href={yvCmsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Go to YouVisit CMS
                </a>
              </li>
              {profileData.showAriaOptimize && (
                <li>
                  <a
                    onClick={goToAriaOptimizeClick}
                    href={optimizeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Go to Aria Optimize
                  </a>
                </li>
              )}
            </ul>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Link to="/logout" tabIndex={-1}>
                <Button
                  buttonType="defaultClear"
                  onClick={() => showProfileMenu(false)}
                >
                  Logout
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}, areEqualObjects);

Profile.defaultProps = {
  classes: {},
};

export default injectSheet(styleSheet)(Profile);
